import {
  autoUpdate,
  computePosition,
  flip,
  FloatingList,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  offset,
  type Placement,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useHover,
  useId,
  useInteractions,
  useListItem,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import c from 'clsx';
import { compact, omit, pick } from 'lodash';
import * as React from 'react';

import Scrolling from '../utils/scrolling';
import Icon, { IconName } from './Icon';

type MenuContextType = {
  getItemProps: any;
  activeIndex: number | null;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  isOpen: boolean;
  isActive: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
  openChildId: string | null;
  setOpenChildId: React.Dispatch<React.SetStateAction<string | null>>;
  isHoverLocked: boolean;
  setIsHoverLocked: React.Dispatch<React.SetStateAction<boolean>>;
  parent: MenuContextType | null;
};

const MenuContext = React.createContext<MenuContextType>({
  getItemProps: () => ({}),
  activeIndex: null,
  setActiveIndex: () => {},
  isOpen: false,
  isActive: false,
  setIsOpen: () => {},
  setIsActive: () => {},
  openChildId: null,
  setOpenChildId: () => {},
  isHoverLocked: false,
  setIsHoverLocked: () => {},
  parent: null,
});

interface MenuProps {
  key?: string;
  icon?: IconName | undefined;
  label?: string;
  description?: string | undefined;
  nested?: boolean;
  openOnHover?: boolean;
  disableHover?: boolean;
  children?: React.ReactNode;
  referenceElement?: HTMLElement | undefined;
  referenceOffset?: number;
  onOpen?: (() => void | undefined) | undefined;
  onClose?: (() => void | undefined) | undefined;
  buttonClassNames?: string;
  dropdownClassNames?: string;
  closeOnParentScroll?: boolean;
  // Element to portal the menu into instead of <body> (see utils/floating_root). Submenus
  // follow their parent's portal, so only the top-level menu needs it.
  portalRoot?: HTMLElement | undefined;
}

export const Menu = React.forwardRef<HTMLButtonElement, MenuProps & React.HTMLProps<HTMLButtonElement>>(
  (
    {
      children,
      label,
      icon,
      description,
      onOpen,
      onClose,
      referenceElement,
      referenceOffset = 10,
      openOnHover,
      disableHover,
      buttonClassNames,
      dropdownClassNames,
      closeOnParentScroll = false,
      portalRoot,
      ...props
    },
    forwardedRef,
  ) => {
    const handleOpen = onOpen || (() => {});
    const handleClose = onClose || (() => {});

    const [isOpen, setIsOpen] = React.useState(!!referenceElement);
    const [isActive, setIsActive] = React.useState(!!referenceElement);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [scrolling, setScrolling] = React.useState<any>(null);
    const [openChildId, setOpenChildId] = React.useState<string | null>(null);
    const [isHoverLocked, setIsHoverLocked] = React.useState(false);
    const [manualPlacement, setManualPlacement] = React.useState<Placement | null>(null);
    const lockedPlacementRef = React.useRef<Placement | null>(null);

    const id = useId();
    const elementsRef = React.useRef<Array<HTMLButtonElement | null>>([]);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const parent = React.useContext(MenuContext);

    const tree = useFloatingTree();
    const nodeId = useFloatingNodeId();
    const parentId = useFloatingParentNodeId();
    const item = useListItem();

    const isNested = parentId != null;

    const { floatingStyles, refs, context, placement } = useFloating<HTMLButtonElement>({
      nodeId,
      open: isOpen,
      onOpenChange: setIsOpen,
      ...(!referenceElement
        ? {
            // Fixed, like the menu anchored to `referenceElement`, so a portal root that clips its
            // overflow can't cut a submenu off.
            strategy: 'fixed',
            placement: isNested ? 'right-start' : 'bottom-start',
            middleware: [
              offset({
                mainAxis: isNested ? 8 : 4,
                alignmentAxis: isNested ? -4 : 0,
              }),
              flip(),
              shift(),
            ],
            whileElementsMounted: autoUpdate,
          }
        : {}),
    });

    if (referenceElement) {
      React.useEffect(() => {
        refs.setReference(referenceElement);
        if (!refs.floating.current || !isOpen) return;

        const cleanup = autoUpdate(referenceElement, refs.floating.current, () => {
          const floatingEl = refs.floating.current;
          if (!floatingEl) return;
          const stepContainer =
            referenceElement.closest('#step-live-component') || document.getElementById('step-live-component');
          const referenceRect = referenceElement.getBoundingClientRect();
          const stepRect = stepContainer?.getBoundingClientRect();
          const menuHeight = floatingEl.scrollHeight || floatingEl.offsetHeight || 320;
          const availableBottom = stepRect
            ? Math.max(0, stepRect.bottom - referenceRect.bottom)
            : Math.max(0, window.innerHeight - referenceRect.bottom);
          const availableTop = stepRect
            ? Math.max(0, referenceRect.top - stepRect.top)
            : Math.max(0, referenceRect.top);

          const nextPlacement: Placement =
            lockedPlacementRef.current ||
            (availableBottom < menuHeight + referenceOffset && availableTop > availableBottom
              ? 'top-start'
              : 'bottom-start');

          lockedPlacementRef.current = nextPlacement;

          computePosition(referenceElement, floatingEl, {
            placement: nextPlacement,
            strategy: 'fixed',
            middleware: [offset({ mainAxis: referenceOffset, alignmentAxis: 0 }), shift({ padding: 8 })],
          }).then(({ x, y, placement: resolvedPlacement }) => {
            if (!floatingEl || !floatingEl.isConnected) return;
            setManualPlacement(resolvedPlacement);
            Object.assign(floatingEl.style, {
              position: 'fixed',
              left: `${x}px`,
              top: `${y}px`,
            });
          });
        });

        return cleanup;
      }, [referenceElement, referenceOffset, refs.floating.current, isOpen]);
    }

    React.useEffect(() => {
      if (isOpen) return;
      lockedPlacementRef.current = null;
    }, [isOpen]);

    React.useEffect(() => {
      if (!referenceElement && manualPlacement !== null) {
        setManualPlacement(null);
      }
    }, [referenceElement, manualPlacement]);

    const hover = useHover(context, {
      enabled: isNested,
      // Note: We need to delay the close event to prevent the menu from closing when the user
      // moves the mouse outside the entire dropdown component. Hovering over different menu submenus will close the current one immidiatelly.
      delay: { open: 75, close: 1000_000_000 },
      handleClose: safePolygon({ blockPointerEvents: true }),
    });

    const click = useClick(context, {
      event: 'mousedown',
      toggle: !isNested,
      ignoreMouse: isNested,
    });

    const role = useRole(context, { role: 'listbox' });
    const dismiss = useDismiss(context, { bubbles: true });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([hover, click, role, dismiss]);

    // Handle callbacks
    React.useEffect(() => {
      if (isOpen) handleOpen();
      if (!isOpen) handleClose();
    }, [isOpen]);

    // Handle menu activation
    React.useEffect(() => {
      if (!tree) return;

      if (isActive) tree.events.emit('menuActivated', { nodeId, parentId });
    }, [tree, isActive, parentId]);

    React.useEffect(() => {
      if (!tree) return;

      const onMenuActivated = (event: { nodeId: string; parentId: string | null }) => {
        if (event.nodeId !== nodeId && event.parentId === parentId) {
          setIsActive(false);
        }
      };

      tree.events.on('menuActivated', onMenuActivated);

      return () => {
        tree?.events.off('menuActivated', onMenuActivated);
      };
    }, [tree]);

    // Event emitter allows you to communicate across tree components.
    // This effect closes all menus when an item gets clicked anywhere
    // in the tree.
    React.useEffect(() => {
      if (!tree) return;

      function handleTreeClick() {
        setIsOpen(false);
      }

      function onSubMenuOpen(event: { nodeId: string; parentId: string }) {
        if (event.parentId === nodeId) {
          setOpenChildId(event.nodeId);
          setIsActive(false);
        } else if (event.nodeId !== nodeId && event.parentId === parentId) {
          setIsOpen(false);
        }
      }

      function openSubMenu(event: { elementId: string }) {
        if (event.elementId === id) {
          setIsOpen(true);
          setIsActive(true);
          if (disableHover && parent) {
            parent.setIsActive(false);
            parent.setOpenChildId(nodeId);
          }
        }
      }

      tree.events.on('click', handleTreeClick);
      tree.events.on('menuOpened', onSubMenuOpen);
      tree.events.on('openMenu', openSubMenu);

      return () => {
        tree.events.off('click', handleTreeClick);
        tree.events.off('menuOpened', onSubMenuOpen);
        tree.events.off('openMenu', openSubMenu);
      };
    }, [tree, nodeId, parentId]);

    // Lock scrolling when menu is open
    React.useEffect(() => {
      setScrolling(
        referenceElement && refs.floating.current ? new Scrolling(refs.floating.current.parentElement) : null,
      );
    }, [referenceElement, refs.floating.current]);

    React.useEffect(() => {
      if (isOpen) {
        scrolling?.disable();
      } else {
        scrolling?.enable();
      }

      return () => {
        scrolling?.enable();
      };
    }, [scrolling, isOpen]);

    React.useEffect(() => {
      if (isOpen && tree) {
        tree.events.emit('menuOpened', { parentId, nodeId });
      }
    }, [tree, isOpen, nodeId, parentId, scrolling]);

    React.useEffect(() => {
      if (isOpen) return;
      if (!parent) return;
      parent.setOpenChildId(prev => (prev === nodeId ? null : prev));
    }, [isOpen, parent, nodeId]);

    // Delegated keyboard events
    React.useEffect(() => {
      if (!tree) return;
      if (!isActive) return;

      const onKeyDown = (event: KeyboardEvent) => {
        const elements = compact(elementsRef.current);
        if (elements.length === 0) return;
        if (openChildId) return;

        const minIndex = 0;
        const maxIndex = elements.length - 1;

        if (event.key === 'ArrowDown') {
          setActiveIndex(index => (index >= maxIndex ? minIndex : index + 1));
        }

        if (event.key === 'ArrowUp') {
          setActiveIndex(index => (index <= minIndex ? maxIndex : index - 1));
        }

        if (event.key === 'ArrowRight') {
          const element = elements[activeIndex];

          if (element) {
            if (element.getAttribute('data-submenu') === 'true') {
              setOpenChildId(element.id);
              setIsActive(false);
            }
            tree.events.emit('openMenu', { elementId: element.id });
          }
        }

        if (event.key === 'ArrowLeft' && parentId !== null && !referenceElement) {
          parent.setIsActive(true);
          setIsOpen(false);
          setIsActive(false);
          parent.setOpenChildId(null);
        }

        if (event.key === 'Enter' && activeIndex !== null) {
          elements[activeIndex]?.click();
        }
      };

      tree.events.on('keyDown', onKeyDown);

      return () => {
        tree.events.off('keyDown', onKeyDown);
      };
    }, [tree, isActive, activeIndex, openChildId, parentId, referenceElement]);

    const isParentActive = parent.isActive && parent.activeIndex == item.index;

    React.useEffect(() => {
      if (isParentActive) {
        buttonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }, [isParentActive, buttonRef.current]);

    React.useEffect(() => {
      if (!isNested) return;
      if (!parent.isActive) return;
      if (!isParentActive && isOpen) {
        setIsOpen(false);
      }
    }, [isNested, parent.isActive, isParentActive, isOpen]);

    React.useEffect(() => {
      if (!closeOnParentScroll) return;
      if (!isNested) return;
      if (!isOpen) return;

      const referenceEl = refs.reference.current;
      if (!(referenceEl instanceof HTMLElement)) return;

      const parentMenu = referenceEl.closest('.mention-menu');
      if (!parentMenu) return;

      const onParentScroll = () => {
        setIsOpen(false);
        setIsActive(false);
        parent.setOpenChildId(prev => (prev === nodeId ? null : prev));
      };

      parentMenu.addEventListener('scroll', onParentScroll, { passive: true });

      return () => {
        parentMenu.removeEventListener('scroll', onParentScroll);
      };
    }, [closeOnParentScroll, isNested, isOpen, refs.reference, parent, nodeId]);

    React.useEffect(() => {
      if (!isOpen) return;

      const floatingEl = refs.floating.current;
      if (!(floatingEl instanceof HTMLElement)) return;

      const onWheel = (event: WheelEvent) => {
        if (event.deltaY === 0) return;

        const { scrollTop, scrollHeight, clientHeight } = floatingEl;
        if (scrollHeight <= clientHeight) return;

        const atTop = scrollTop <= 0;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

        if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) {
          event.preventDefault();
          event.stopPropagation();
        }
      };

      const onScroll = () => {
        setIsHoverLocked(true);
        setIsActive(false);
        setOpenChildId(null);
      };

      const onMouseMove = () => {
        setIsHoverLocked(false);
      };

      floatingEl.addEventListener('wheel', onWheel, { passive: false });
      floatingEl.addEventListener('scroll', onScroll, { passive: true });
      floatingEl.addEventListener('mousemove', onMouseMove);

      return () => {
        floatingEl.removeEventListener('wheel', onWheel);
        floatingEl.removeEventListener('scroll', onScroll);
        floatingEl.removeEventListener('mousemove', onMouseMove);
      };
    }, [isOpen, refs.floating]);

    return (
      <FloatingNode id={nodeId}>
        {!referenceElement && (
          <button
            type='button'
            id={id}
            ref={useMergeRefs([refs.setReference, item.ref, forwardedRef, buttonRef])}
            role={isNested ? 'menuitem' : undefined}
            data-submenu={isNested ? undefined : true}
            tabIndex={-1}
            aria-autocomplete='list'
            aria-selected={parent.activeIndex === item.index}
            data-open={isOpen ? '' : undefined}
            className={c(
              'flex items-center gap-x-2 rounded-sm px-2 py-2 text-left text-base hover:bg-gray-100',
              buttonClassNames,
              {
                'bg-gray-100': parent.activeIndex == item.index && (parent.isActive || isOpen),
              },
            )}
            {...getReferenceProps({
              ...parent.getItemProps({
                ...omit(props, 'buttonClassNames'),
              }),
              onMouseEnter(event: React.MouseEvent<HTMLButtonElement>) {
                if (disableHover) {
                  props.onMouseEnter?.(event);
                  return;
                }
                if (isHoverLocked) {
                  props.onMouseEnter?.(event);
                  return;
                }
                props.onMouseEnter?.(event);
                parent.setIsActive(true);
                parent.setActiveIndex(item.index);
                if (openOnHover || isNested) {
                  tree?.events.emit('openMenu', { elementId: id });
                }
              },
            })}
          >
            {icon && <Icon name={icon} className='h-5 w-5 flex-shrink-0 text-gray-400' />}
            <div className='grow truncate'>{label}</div>
            <div className='truncate text-gray-400'>{description}</div>
            {isNested && <Icon name='hero-chevron-right' className='h-4 w-4 flex-shrink-0' />}
          </button>
        )}
        <MenuContext.Provider
          value={{
            activeIndex,
            setActiveIndex,
            getItemProps,
            isOpen,
            isActive,
            setIsOpen,
            setIsActive,
            openChildId,
            setOpenChildId,
            isHoverLocked,
            setIsHoverLocked,
            parent,
          }}
        >
          <FloatingList elementsRef={elementsRef}>
            {isOpen && (
              <FloatingPortal {...(portalRoot ? { root: portalRoot } : {})}>
                <div
                  ref={refs.setFloating}
                  data-placement={manualPlacement ?? placement}
                  className={c(
                    'z-50 flex transform flex-col gap-y-1 overflow-y-auto rounded-sm bg-white p-1 text-gray-900 shadow-md ring-1 ring-gray-200',
                    dropdownClassNames,
                  )}
                  style={{
                    ...(referenceElement ? {} : floatingStyles),
                    minWidth: '220px',
                    maxWidth: '400px',
                    maxHeight: '320px',
                  }}
                  {...getFloatingProps()}
                >
                  {children}
                </div>
              </FloatingPortal>
            )}
          </FloatingList>
        </MenuContext.Provider>
      </FloatingNode>
    );
  },
);

interface MenuItemProps {
  key?: string;
  icon?: IconName | undefined;
  label: string;
  description?: string | undefined;
  disabled?: boolean;
  className?: string;
  disableHover?: boolean;
  // For in-menu actions (e.g. paging) that should not dismiss the dropdown.
  keepOpenOnClick?: boolean;
}

export const MenuItem = React.forwardRef<
  HTMLButtonElement,
  MenuItemProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ label, icon, description, disabled, keepOpenOnClick, children, ...props }, forwardedRef) => {
  const id = useId();
  const menu = React.useContext(MenuContext);
  const item = useListItem({ label: disabled ? null : label });
  const tree = useFloatingTree();
  const isActive = menu.isActive && item.index === menu.activeIndex;
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (isActive) {
      buttonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [isActive, buttonRef.current]);

  return (
    <button
      id={id}
      {...pick(props, ['key', 'disabled'])}
      ref={useMergeRefs([item.ref, forwardedRef, buttonRef])}
      type='button'
      role='option'
      className={c(
        'flex items-center gap-x-2 rounded-sm py-2 pl-2 pr-3 text-left hover:bg-gray-100 focus:bg-gray-100',
        props.className,
        { 'opacity-40': disabled, 'bg-gray-100': isActive },
      )}
      tabIndex={-1}
      aria-selected={isActive}
      aria-disabled={disabled}
      {...menu.getItemProps({
        onClick(event: React.MouseEvent<HTMLButtonElement>) {
          props.onClick?.(event);
          if (!keepOpenOnClick) tree?.events.emit('click');
          event.stopPropagation();
        },
        onMouseDown(event: React.MouseEvent<HTMLButtonElement>) {
          props.onMouseDown?.(event);
        },
        onMouseEnter(event: React.MouseEvent<HTMLButtonElement>) {
          if (props.disableHover) {
            props.onMouseEnter?.(event);
            return;
          }
          if (menu.isHoverLocked) {
            props.onMouseEnter?.(event);
            return;
          }
          props.onMouseEnter?.(event);
          if (menu.isOpen) {
            menu.setIsActive(true);
            menu.setActiveIndex(item.index);
          }
        },
      })}
    >
      {children ? (
        children
      ) : (
        <>
          {icon && <Icon name={icon} className='ml-1 h-5 w-5 flex-shrink-0 text-gray-400' />}
          <div className='truncate text-base'>{label}</div>
          {description && (
            <div className='ml-4 flex-shrink-0 grow text-right capitalize text-gray-400'>{description}</div>
          )}
        </>
      )}
    </button>
  );
});

interface InlineMenuProps {
  key?: string;
  icon?: IconName | undefined;
  label?: string;
  description?: string | undefined;
  children?: React.ReactNode;
  titleClassNames?: string;
  dropdownClassNames?: string;
}

export const InlineMenu = React.forwardRef<HTMLButtonElement, InlineMenuProps & React.HTMLProps<HTMLButtonElement>>(
  ({ children, label, icon, description, ...props }, _forwardedRef) => {
    return (
      <div>
        <div
          className={`disabled flex cursor-default items-center gap-x-2 rounded-sm py-1.5 pl-3 pr-2 text-left text-gray-500 ${props.titleClassNames}`}
        >
          {icon && <Icon name={icon} className='h-4 w-4 flex-shrink-0' />}
          <div className='grow truncate'>{label}</div>
          <div className='truncate text-gray-400'>{description}</div>
        </div>

        <div
          className={`my-2 ml-[19px] flex flex-col gap-y-1 border-l border-gray-200 px-1 text-gray-900 ${props.dropdownClassNames}`}
          style={{
            maxWidth: '400px',
            maxHeight: '450px',
          }}
        >
          {children}
        </div>
      </div>
    );
  },
);

export const Search = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  return (
    <input
      ref={ref}
      className='ring-base-200 focus:outline-hidden m-1 rounded-md border-none px-2 py-1 ring-1 focus:ring-indigo-500'
      placeholder='Search ...'
      autoFocus={true}
      {...props}
    />
  );
});

const DropdownComponent = React.forwardRef<DropdownRef, MenuProps & React.HTMLProps<HTMLButtonElement>>(
  (props, ref) => {
    const tree = useFloatingTree();

    React.useImperativeHandle(ref, () => ({
      onKeyDown(event: KeyboardEvent) {
        tree?.events.emit('keyDown', event);
      },
    }));

    return <Menu {...props}>{props.children}</Menu>;
  },
);

export type DropdownRef = {
  onKeyDown(event: KeyboardEvent): void;
};

export const Dropdown = React.forwardRef<
  DropdownRef,
  MenuProps & { referenceElement: Element } & React.HTMLProps<HTMLButtonElement>
>((props, ref) => {
  return (
    <FloatingTree>
      <DropdownComponent {...props} ref={ref} />
    </FloatingTree>
  );
});
